import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { logo_new } from '../assets';
import { COMMON_MUI_STYLES } from '../styles';
import { useAppContext } from '../context/AppContext';

const outlinedActionButtonSx = {
  textTransform: 'none',
  fontWeight: 500,
  lineHeight: 'normal',
  borderRadius: '8px',
  minHeight: '2.75rem',
  fontSize: {
    xs: '1rem',
    sm: '1.1rem',
    md: '1.2rem',
  },
  padding: {
    xs: '12px 24px',
    sm: '14px 28px',
    md: '10px 32px',
  },
  width: {
    xs: '80%',
    sm: 'auto',
  },
  borderColor: 'var(--color-border-dark)',
  color: 'var(--color-text-primary)',
  '&:hover': {
    borderColor: 'var(--color-border-dark)',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
} as const;

const ErrorPage: React.FC = () => {
  const { setCurrentView } = useAppContext();

  const handleRefresh = () => {
    // Refresh the page or navigate back to home
    window.location.reload();
  };

  const handleGoHome = () => {
    setCurrentView('home');
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'var(--color-background, #ffffff)',
        px: 3,
      }}
    >
      {/* Main Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          maxWidth: '600px',
          width: '100%',
          zIndex: 2,
        }}
      >
        <Box
          component="img"
          src={logo_new}
          alt="HL Skin"
          sx={{
            width: { xs: '70%', sm: '60%', md: '320px' },
            maxWidth: 360,
            height: 'auto',
            objectFit: 'contain',
            mb: 3,
          }}
        />
        {/* Main Heading */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            mb: 4,
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
          }}
        >
          We're experiencing a temporary issue
        </Typography>

        {/* Error Icon */}
        <Box
          sx={{
            width: { xs: '80px', sm: '100px', md: '120px' },
            height: { xs: '80px', sm: '100px', md: '120px' },
            borderRadius: '50%',
            backgroundColor: '#f44336',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 4,
            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
          }}
        >
          <Typography
            sx={{
              color: 'var(--color-text-inverse)',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 700,
            }}
          >
            !
          </Typography>
        </Box>

        {/* Error Message */}
        <Typography
          variant="body1"
          sx={{
            color: 'var(--color-text-primary)',
            mb: 4,
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
            lineHeight: 1.6,
            maxWidth: '500px',
          }}
        >
          Sorry for the inconvenience, our servers are currently down.
          <br />
          We're working to resolve this as quickly as possible.
          <br />
          Please check back soon, and thank you for your patience.
        </Typography>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Refresh Button */}
          <Button
            variant="contained"
            onClick={handleRefresh}
            startIcon={<RefreshIcon />}
            sx={COMMON_MUI_STYLES.primaryCtaButton2}
          >
            Refresh
          </Button>

          {/* Go Home Button */}
          <Button
            variant="outlined"
            onClick={handleGoHome}
            sx={outlinedActionButtonSx}
          >
            Go Home
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ErrorPage;